/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import React from "react";

import { useTranslation } from "react-i18next";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import useResponse from "@/shared/hooks/useResponse";
import { useTableStoreByRoute } from "@/stores/useTableStore";
import MuiTableCell from "@mui/material/TableCell";
import MuiTableHead from "@mui/material/TableHead";
import MuiTableRow from "@mui/material/TableRow";

import {
  Column,
  ROUTES,
  tableColumnDropShadow,
  tableColumnRightDropShadow,
  TableRowData
} from "../../../../../shared";
import TableSortLabel from "../../../../TableSortLabel/TableSortLabel";
import TableCheckbox from "./TableCheckbox";

interface ITableHeadProps {
  route: ROUTES;
  scrollHorizontal: boolean;
  companiesPage?: boolean;
}

const TableHead = ({
  route,
  scrollHorizontal,
  companiesPage
}: ITableHeadProps) => {
  const { t } = useTranslation();

  const { getData } = useAxiosPrivate();
  const { handleResponse } = useResponse();
  const {
    isCheckable,
    columns,
    selectedRows,
    selectableRows,
    setSelectedRows,
    inlineActions,
    params,
    sortColumn,
    fetchData
  } = useTableStoreByRoute(route);

  const { orderBy, descending } = params.sortedColumn || {
    orderBy: "",
    descending: true
  };

  const setLeftPosition = (isSticky: boolean | undefined) =>
    isSticky ? "60px" : "unset";

  const handletableHeadCheckboxChange = () => {
    setSelectedRows(
      selectableRows.map((row: TableRowData) => row.id as string)
    );
  };

  const isChecked = () =>
    selectedRows.length > 0 &&
    selectedRows.every((rowId: string) =>
      selectableRows.some((row: TableRowData) => row.id === rowId)
    );

  const handleSorting = (sortElement: string) => {
    sortColumn(sortElement);
    fetchData(getData, handleResponse);
  };

  return (
    <MuiTableHead data-testid="table-head">
      <MuiTableRow>
        {isCheckable && (
          <TableCheckbox
            route={route}
            onTableHeadCheckboxChange={handletableHeadCheckboxChange}
            isChecked={isChecked()}
            disabled={selectableRows?.length === 0}
          />
        )}
        {columns.map(
          ({ name, isSticky, width, sortResource, isActive }: Column) => {
            const isActiveSorting = orderBy === sortResource;

            const isDescending = isActiveSorting ? descending : true;

            return (
              isActive && (
                <MuiTableCell
                  sx={{ minWidth: width }}
                  css={css([
                    {
                      left: isCheckable ? setLeftPosition(isSticky) : "0",
                      zIndex: isSticky ? "3" : "2"
                    },
                    isSticky && tableColumnRightDropShadow(scrollHorizontal)
                  ])}
                  key={name}
                >
                  {!companiesPage && sortResource ? (
                    <TableSortLabel
                      isActive={isActiveSorting}
                      isDescending={isDescending}
                      onSortClick={() => handleSorting(sortResource)}
                    >
                      {t(`Table##${name.toLowerCase()}`)}
                    </TableSortLabel>
                  ) : (
                    !companiesPage && t(`Table##${name.toLowerCase()}`)
                  )}
                </MuiTableCell>
              )
            );
          }
        )}
        {/* Add drop shadow on horizontal scroll */}
        {inlineActions?.length > 0 && (
          <MuiTableCell
            sx={[
              {
                position: "sticky",
                right: "0",
                width: "0 !important",
                padding: "0 !important"
              },
              tableColumnDropShadow(scrollHorizontal)
            ]}
          />
        )}
      </MuiTableRow>
    </MuiTableHead>
  );
};

export default React.memo(TableHead);
